// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * Pure helpers for measuring and tidying the SVG that potrace returns.
 * These are the tool's analytical core and are unit-tested directly — they must
 * never touch the DOM so they run identically in a worker, jsdom or the page.
 */

/** Count `<path …>` elements. */
export function countPaths(svg: string): number {
  const m = svg.match(/<path\b/gi);
  return m ? m.length : 0;
}

/** Collect the distinct visible fill colours used (excludes none/transparent). */
export function countColors(svg: string): number {
  return colorList(svg).length;
}

export function colorList(svg: string): string[] {
  const seen = new Set<string>();
  const re = /fill\s*=\s*"([^"]*)"/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(svg)) !== null) {
    const v = m[1].trim().toLowerCase();
    if (!v || v === 'none' || v === 'transparent') continue;
    seen.add(normalizeColor(v));
  }
  return [...seen];
}

function normalizeColor(v: string): string {
  // Expand #abc → #aabbcc so equal colours in different forms de-dupe.
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(v);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return v;
}

/** Read the intrinsic width/height from the viewBox (or width/height attrs). */
export function svgDimensions(svg: string): { width: number; height: number } {
  const vb = /viewBox\s*=\s*"([-\d.eE\s]+)"/.exec(svg);
  if (vb) {
    const parts = vb[1].trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
      return { width: Math.abs(parts[2]), height: Math.abs(parts[3]) };
    }
  }
  const w = /\bwidth\s*=\s*"([\d.]+)/.exec(svg);
  const h = /\bheight\s*=\s*"([\d.]+)/.exec(svg);
  return { width: w ? parseFloat(w[1]) : 0, height: h ? parseFloat(h[1]) : 0 };
}

/**
 * Round the numbers inside geometry attributes (`d`, `points`, `transform`) to
 * `precision` decimals and strip redundant whitespace between tags. Colours,
 * ids and other attribute values are left untouched, so this never corrupts a
 * hex fill. Returns a byte-for-byte smaller but visually identical SVG.
 */
export function minifySvg(svg: string, precision = 2): string {
  let out = svg
    .replace(/<\?xml[^>]*\?>/i, '')
    .replace(/<!DOCTYPE[^>]*>/i, '')
    .replace(/<!--[\s\S]*?-->/g, '');
  out = out.replace(/(\b(?:d|points|transform)\s*=\s*")([^"]*)(")/gi, (_all, pre, body, post) => {
    return pre + roundNumbers(body, precision) + post;
  });
  // Collapse newlines/indentation between tags.
  out = out.replace(/>\s+</g, '><').trim();
  return out;
}

function roundNumbers(s: string, precision: number): string {
  return s.replace(/-?\d*\.?\d+(?:[eE][-+]?\d+)?/g, (num) => {
    const n = Number(num);
    if (!Number.isFinite(n)) return num;
    // toFixed then drop trailing zeros and a lone trailing dot.
    let r = n.toFixed(precision);
    if (r.includes('.')) r = r.replace(/0+$/, '').replace(/\.$/, '');
    if (r === '-0') r = '0';
    return r;
  });
}

/**
 * Ensure the SVG has a namespace, a viewBox and no fixed pixel width/height so
 * it scales cleanly wherever it is dropped. Idempotent.
 */
export function normalizeSvg(svg: string): string {
  let out = svg.trim();
  if (!/xmlns\s*=/.test(out)) {
    out = out.replace(/<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"');
  }
  return out;
}
